import * as React from 'react'
import { useConsent } from '../consent/ConsentProvider'
import {
  CONSENT_CATEGORIES,
  ConsentChoices,
  REJECTED_CHOICES,
} from '../consent/types'

/**
 * GDPR / ePrivacy cookie consent UI, rendered by the app shell.
 *
 * Two modes share one component:
 *  - the first-run banner (no valid decision yet), and
 *  - the granular preferences dialog (opened here or from the footer link).
 *
 * Compliance choices worth calling out:
 *  - Non-necessary categories default OFF (no pre-ticked boxes, no implied
 *    consent from scrolling or dismissing).
 *  - "Tout refuser" is as prominent and as easy as "Tout accepter".
 *  - The banner cannot be closed without an explicit choice — there is no "X".
 *  - Consent is versioned + timestamped and can be withdrawn/changed anytime.
 */
const CookieBanner: React.FC = () => {
  const {
    needsDecision,
    isPanelOpen,
    record,
    openPreferences,
    closePreferences,
    save,
    acceptAll,
    rejectAll,
  } = useConsent()

  // Granular toggles, seeded from the stored record (when re-managing) or from
  // the all-rejected default (first run).
  const [draft, setDraft] = React.useState<ConsentChoices>(
    () => record?.choices ?? REJECTED_CHOICES
  )

  // Re-seed whenever the panel opens so it always reflects the latest record.
  React.useEffect(() => {
    if (isPanelOpen) setDraft(record?.choices ?? REJECTED_CHOICES)
  }, [isPanelOpen, record])

  const isVisible = needsDecision || isPanelOpen

  // While the modal consent layer is up, lock background scroll so the page
  // behind the scrim is fully inert (the scrim itself blocks pointer events).
  React.useEffect(() => {
    if (!isVisible) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [isVisible])

  const dialogRef = React.useRef<HTMLDivElement>(null)

  // Esc closes the preferences panel. If the user hasn't decided yet, the
  // first-run banner stays up — Esc must not count as consent.
  React.useEffect(() => {
    if (!isPanelOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePreferences()
    }
    window.addEventListener('keydown', onKey)
    dialogRef.current?.focus()
    return () => window.removeEventListener('keydown', onKey)
  }, [isPanelOpen, closePreferences])

  // Show nothing once a valid decision exists and the panel is closed.
  if (!isVisible) return null

  const toggle = (id: 'analytics' | 'marketing') =>
    setDraft(prev => ({ ...prev, [id]: !prev[id] }))

  return (
    <div className='CookieConsent' role='region' aria-label='Gestion des cookies'>
      {isPanelOpen
        ? (
          <div
            className='CookieConsent__dialog'
            role='dialog'
            aria-modal='true'
            aria-labelledby='cookie-consent-title'
            tabIndex={-1}
            ref={dialogRef}
          >
            <h2 id='cookie-consent-title' className='CookieConsent__title'>
              Vos préférences de cookies
            </h2>
            <p className='CookieConsent__lead'>
              Nous utilisons des cookies pour faire fonctionner le site et, avec votre accord,
              mesurer l’audience et personnaliser les contenus. Vous pouvez modifier vos choix à
              tout moment.
            </p>

            <ul className='CookieConsent__categories'>
              {CONSENT_CATEGORIES.map(cat => {
                const checked = cat.required ? true : draft[cat.id as 'analytics' | 'marketing']
                return (
                  <li key={cat.id} className='CookieConsent__category'>
                    <label className='CookieConsent__categoryHead'>
                      <input
                        type='checkbox'
                        checked={checked}
                        disabled={cat.required}
                        onChange={() => !cat.required && toggle(cat.id as 'analytics' | 'marketing')}
                      />
                      <span className='CookieConsent__categoryTitle'>{cat.title}</span>
                      {cat.required && (
                        <span className='CookieConsent__badge'>Toujours actif</span>
                      )}
                    </label>
                    <p className='CookieConsent__categoryDesc'>{cat.description}</p>
                  </li>
                )
              })}
            </ul>

            <div className='CookieConsent__actions'>
              <button type='button' className='CookieConsent__btn CookieConsent__btn--ghost' onClick={rejectAll}>
                Tout refuser
              </button>
              <button type='button' className='CookieConsent__btn CookieConsent__btn--ghost' onClick={() => save(draft)}>
                Enregistrer mes choix
              </button>
              <button type='button' className='CookieConsent__btn CookieConsent__btn--primary' onClick={acceptAll}>
                Tout accepter
              </button>
            </div>

            <p className='CookieConsent__legal'>
              En savoir plus dans notre{' '}
              <a href='/privacy' className='CookieConsent__link'>politique de confidentialité</a>.
            </p>
          </div>
          )
        : (
          <div
            className='CookieConsent__banner'
            role='dialog'
            aria-modal='false'
            aria-labelledby='cookie-consent-title'
            aria-describedby='cookie-consent-desc'
          >
            <div className='CookieConsent__text'>
              <h2 id='cookie-consent-title' className='CookieConsent__title'>
                Nous respectons votre vie privée
              </h2>
              <p id='cookie-consent-desc' className='CookieConsent__lead'>
                Nous utilisons des cookies nécessaires au fonctionnement du site et, avec votre
                accord, des cookies de mesure d’audience et de personnalisation. Vous pouvez
                accepter, refuser ou paramétrer votre choix.
              </p>
            </div>
            <div className='CookieConsent__actions'>
              <button type='button' className='CookieConsent__btn CookieConsent__btn--ghost' onClick={openPreferences}>
                Personnaliser
              </button>
              <button type='button' className='CookieConsent__btn CookieConsent__btn--ghost' onClick={rejectAll}>
                Tout refuser
              </button>
              <button type='button' className='CookieConsent__btn CookieConsent__btn--primary' onClick={acceptAll}>
                Tout accepter
              </button>
            </div>
          </div>
          )}
    </div>
  )
}

export default CookieBanner
